import React from 'react';
import {
    Flex,
    ReadMore,
    useStyles,
    pxToRem,
    pxArrayToRem,
    Grid,
    useAppDisplayModeContext,
    ProfileNavigation,
    useTheme,
} from '@archipro-design/aria';
import { Link } from '@remix-run/react';
import * as S from './Content.style';
import { useIsSafari } from '@modules/root/hook/use-is-safari';
import { ArrowRightLine } from '@archipro-design/icons';
import Stats from './Stats';

import CompanyInfoStats from './CompanyInfoStats';
import { useTracker } from '@archipro-website/tracker';
import { fireProfessionalLinkEvent } from '../../util/tracker-helpers';
import { useWebsiteTrackingProxy } from '~/modules/tracking/hook/useWebsiteTrackingProxy';
import { useLoaderData } from '@remix-run/react';
import type { ProjectPageLoader } from '@modules/project/type';
import type { loader } from '~/routes/_app.project.$urlSegment._index';
import { PROFESSIONAL_SECTIONS_ANCHOR } from '~/modules/professional/util/constants';

const MAX_LINES = 15;

interface ContentProps {
    data: ProjectPageLoader['project']['Professional'];
}

interface ListProps {
    label: string;
    link: string;
}

const Content = ({ data }: ContentProps) => {
    const [visible, setVisible] = React.useState(false);
    const isSafari = useIsSafari();
    const { css } = useStyles({ isSafari, visible });
    const theme = useTheme();
    const { mobile } = useAppDisplayModeContext();
    const tracker = useTracker();
    const links = generateLinks(data, mobile);
    const { project } = useLoaderData<typeof loader>();
    const clickProxy = useWebsiteTrackingProxy({
        professionalID: project.Professional.ID,
        professionalTitle: project.Professional.Title,
        itemType: 'Project',
    });

    return (
        <Flex column={false} vAlign="center">
            <Grid
                columns={pxArrayToRem([880, 896])}
                className={css({
                    columnGap: pxToRem(44),
                })}
            >
                {mobile ? (
                    <ProfileNavigation
                        className={css(S.Tabs)}
                        items={links.map((link) => ({
                            as: 'a',
                            href: link.link,
                            children: link.label,
                            outlined: true,
                            color: 'white',
                            key: link.label,
                        }))}
                    />
                ) : (
                    <div>
                        <div
                            onMouseEnter={() => setVisible(true)}
                            onMouseLeave={() => setVisible(false)}
                            className="block w-[27.125rem] mb-[8rem]"
                        >
                            {links.map((link) => (
                                <Link
                                    to={link.link}
                                    key={link.label}
                                    prefetch="render"
                                    onClick={() => {
                                        fireProfessionalLinkEvent(
                                            link.label,
                                            window.location.href,
                                            tracker
                                        );
                                    }}
                                    className={`
                    flex items-center mb-0 h-[3.375rem] w-full
                    border-b transition-colors duration-300 group
                    ${
                        visible
                            ? 'border-black/50 hover:border-black'
                            : 'border-black hover:border-black/50'
                    }
                `}
                                >
                                    <span
                                        className={`
                        flex-1 text-[1.25rem] text-charcoal-000 tracking-[-0.01em]
                        transition-opacity duration-300
                        ${visible ? 'opacity-50 group-hover:opacity-100' : 'opacity-100 group-hover:opacity-50'}
                    `}
                                    >
                                        {link.label}
                                    </span>
                                    <ArrowRightLine
                                        className={`
                        relative -bottom-[0.0625rem] w-[1.875rem] h-[1.875rem]
                        opacity-0 mr-[0.5rem]
                        group-hover:opacity-100 group-hover:mr-0
                        transition-[opacity,margin] duration-300 ease-in-out
                    `}
                                    />
                                </Link>
                            ))}
                        </div>
                        <Stats data={data} />
                    </div>
                )}

                <div className={css(S.AboutTextContainer)}>
                    <ReadMore
                        content={{
                            children: (
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: data.BlockSummary ?? '',
                                    }}
                                    onClick={clickProxy}
                                    onContextMenu={clickProxy}
                                />
                            ),
                        }}
                        maxLines={MAX_LINES}
                        className={css(S.AboutTextContent)}
                        variables={{
                            toggleFontSize: pxToRem(16),
                            contentLineHeight:
                                theme.siteVariables.lineHeightSmall,
                        }}
                    />
                    <CompanyInfoStats data={data} />
                </div>
            </Grid>
        </Flex>
    );
};

const generateLinks = (
    data: ProjectPageLoader['project']['Professional'],
    mobile: boolean
): ListProps[] => {
    const BASE_COMPANY_URL = `/professional/${data.URLSegment}`;
    return [
        {
            label: mobile ? 'Home' : 'Overview',
            link: BASE_COMPANY_URL,
        },
        {
            label: 'About',
            link: `${BASE_COMPANY_URL}/info#${PROFESSIONAL_SECTIONS_ANCHOR}`,
        },
        ...(data.HasProducts
            ? [
                  {
                      label: 'Products',
                      link: `${BASE_COMPANY_URL}/our-products#${PROFESSIONAL_SECTIONS_ANCHOR}`,
                  },
              ]
            : []),
        {
            label: 'Projects',
            link: `${BASE_COMPANY_URL}/case-studies#${PROFESSIONAL_SECTIONS_ANCHOR}`,
        },
        ...(data.HasArticles
            ? [
                  {
                      label: 'Articles',
                      link: `${BASE_COMPANY_URL}/journal#${PROFESSIONAL_SECTIONS_ANCHOR}`,
                  },
              ]
            : []),
        ...(data.HasVideos
            ? [
                  {
                      label: 'Videos',
                      link: `${BASE_COMPANY_URL}/videos#${PROFESSIONAL_SECTIONS_ANCHOR}`,
                  },
              ]
            : []),
        ...(data.HasPartners
            ? [
                  {
                      label: 'Network',
                      link: `${BASE_COMPANY_URL}/partners#${PROFESSIONAL_SECTIONS_ANCHOR}`,
                  },
              ]
            : []),
        {
            label: 'Contact',
            link: `${BASE_COMPANY_URL}/contact#${PROFESSIONAL_SECTIONS_ANCHOR}`,
        },
    ];
};

export default Content;
